import { useParams } from 'react-router-dom';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useEffect } from 'react';
import { 
  Sparkles, Zap, Building2, Globe, BarChart3, 
  BrainCircuit, Rocket, Inbox, Filter, Users 
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import SEO from '../components/SEO';
import { trackFeaturePageView } from '../services/analytics';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import './FeaturePage.css';
import { useTranslation } from "react-i18next";

interface Step {
  title: string;
  description: string;
  screenshot?: string;
}

interface FeatureData {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  importance: string;
  steps: Step[];
}

const featuresData: Record<string, FeatureData> = {
  'ai-reply-suggestions': {
    slug: 'ai-reply-suggestions',
    title: 'AI Reply Suggestions',
    subtitle: 'Craft the perfect response in seconds.',
    description: 'Reviio automatically analyzes every incoming review and generates three context-aware, on-brand reply suggestions for you to choose from.',
    importance: 'Responding quickly and professionally to reviews is a major ranking factor for local SEO. AI suggestions ensure you never sacrifice quality for speed, allowing you to maintain a consistent brand voice while saving hours of manual typing.',
    icon: Sparkles,
    steps: [
      {
        title: 'View Incoming Reviews',
        description: 'Log into your dashboard and navigate to the Reviews page to see all new feedback.',
        screenshot: '/screenshots/feature-ai-1.png'
      },
      {
        title: 'Review AI Suggestions',
        description: 'For each review, the AI instantly provides three distinct reply options tailored to the sentiment.',
        screenshot: '/screenshots/feature-ai-2.png'
      },
      {
        title: 'Publish or Edit',
        description: 'Select the best suggestion, make minor tweaks if needed, and hit publish directly to Google & Facebook.',
        screenshot: '/screenshots/feature-ai-3.png'
      }
    ]
  },
  'auto-reply-modes': {
    slug: 'auto-reply-modes',
    title: 'Auto-Reply & Custom Rules',
    subtitle: 'Put your review management on autopilot.',
    description: 'Save time by setting up automated replies based on the star rating. Let the AI handle 5-star reviews instantly while you review the rest.',
    importance: 'Customers expect fast responses, often within 24 hours. Automated rules allow you to instantly acknowledge positive feedback, building customer loyalty, while buying you time to personally address negative reviews and de-escalate issues.',
    icon: Zap,
    steps: [
      {
        title: 'Navigate to Settings',
        description: 'Go to your location settings and select the Automation tab.',
        screenshot: '/screenshots/feature-auto-1.png'
      },
      {
        title: 'Configure Star Ratings',
        description: 'Choose which star ratings should trigger an immediate auto-reply and which require manual approval.',
        screenshot: '/screenshots/feature-auto-2.png'
      },
      {
        title: 'Set Delay Window',
        description: 'Optionally set a delay (e.g., 2 hours) before the auto-reply is published so it appears more human.',
        screenshot: '/screenshots/feature-auto-3.png'
      }
    ]
  },
  'multi-location-management': {
    slug: 'multi-location-management',
    title: 'Multi-Location Management',
    subtitle: 'One dashboard for all your branches.',
    description: 'Manage reviews across dozens of locations seamlessly without constantly switching accounts.',
    importance: 'When managing a franchise or multiple branches, keeping track of every location\'s reputation can become a logistical nightmare. A unified dashboard prevents reviews from slipping through the cracks and ensures brand standards are upheld everywhere.',
    icon: Building2,
    steps: [
      {
        title: 'Connect Locations',
        description: 'Link your Google Business Profile and import all your business locations.',
        screenshot: '/screenshots/feature-loc-1.png'
      },
      {
        title: 'Filter by Branch',
        description: 'Use the global dropdown to view metrics or reviews for a specific branch or all combined.',
        screenshot: '/screenshots/feature-loc-2.png'
      },
      {
        title: 'Location-Specific Settings',
        description: 'Apply unique AI tones, languages, and auto-reply rules to each individual location.',
        screenshot: '/screenshots/feature-loc-3.png'
      }
    ]
  },
  'multi-language-support': {
    slug: 'multi-language-support',
    title: 'Multi-Language Support',
    subtitle: 'Speak your customers\' language.',
    description: 'Reviio automatically detects the language of the review and crafts a response in that exact same language.',
    importance: 'In a globalized market or tourist-heavy area, your customers speak many languages. Replying in their native tongue shows immense respect, dramatically improves customer satisfaction, and helps you rank for local SEO keywords in other languages.',
    icon: Globe,
    steps: [
      {
        title: 'Receive Global Reviews',
        description: 'When a customer leaves a review in Spanish, French, or any other language, Reviio detects it.',
        screenshot: '/screenshots/feature-lang-1.png'
      },
      {
        title: 'View Bilingual Preview',
        description: 'Reviio provides the AI reply in the target language along with an English translation for your peace of mind.',
        screenshot: '/screenshots/feature-lang-2.png'
      },
      {
        title: 'Set Default Languages',
        description: 'You can also force the AI to always reply in a specific language regardless of the review.',
        screenshot: '/screenshots/feature-lang-3.png'
      }
    ]
  },
  'review-analytics': {
    slug: 'review-analytics',
    title: 'Review Analytics Dashboard',
    subtitle: 'Track your reputation growth.',
    description: 'Get deep insights into your review volume, average rating, response rate, and team performance over time.',
    importance: 'You can\'t improve what you don\'t measure. Tracking response times and rating trends allows you to identify underperforming locations, measure the ROI of your customer service efforts, and set actionable goals for your team.',
    icon: BarChart3,
    steps: [
      {
        title: 'Access Analytics',
        description: 'Click on the Analytics tab in your dashboard to view high-level KPIs.',
        screenshot: '/screenshots/feature-stat-1.png'
      },
      {
        title: 'Analyze Trends',
        description: 'View beautiful charts showing your rating trends and review velocity over the last 30 or 90 days.',
        screenshot: '/screenshots/feature-stat-2.png'
      },
      {
        title: 'Export Reports',
        description: 'Download CSV reports to share performance metrics with your stakeholders.',
        screenshot: '/screenshots/feature-stat-3.png'
      }
    ]
  },
  'review-insights': {
    slug: 'review-insights',
    title: 'Review Insights',
    subtitle: 'Understand what your customers truly think.',
    description: 'Our AI analyzes all your reviews to extract common themes, sentiment, and areas for operational improvement.',
    importance: 'Reviews are a goldmine of operational feedback. AI-driven insights instantly highlight recurring issues—like cold food or slow service—allowing you to fix root causes before they damage your overall reputation.',
    icon: BrainCircuit,
    steps: [
      {
        title: 'Open Insights Tab',
        description: 'Navigate to the Insights section to see an AI-generated summary of your recent feedback.',
        screenshot: '/screenshots/feature-insight-1.png'
      },
      {
        title: 'Identify Key Topics',
        description: 'See which keywords (e.g., "customer service", "food quality", "wait time") are driving positive or negative sentiment.',
        screenshot: '/screenshots/feature-insight-2.png'
      },
      {
        title: 'Actionable Advice',
        description: 'Read the AI\'s operational recommendations based on your customers\' most frequent complaints.',
        screenshot: '/screenshots/feature-insight-3.png'
      }
    ]
  },
  'review-campaigns': {
    slug: 'review-campaigns',
    title: 'Review Campaigns',
    subtitle: 'Get more 5-star reviews automatically.',
    description: 'Send automated email and SMS requests to your customers asking for feedback after their visit.',
    importance: 'A steady stream of fresh, positive reviews is the #1 way to outrank competitors on Google & Facebook. Proactively asking happy customers for reviews directly translates into more foot traffic and higher revenue.',
    icon: Rocket,
    steps: [
      {
        title: 'Create a Campaign',
        description: 'Go to Campaigns and click "New Campaign". Choose between Email or SMS.',
        screenshot: '/screenshots/feature-camp-1.png'
      },
      {
        title: 'Customize the Template',
        description: 'Personalize the message and add your dynamic review link.',
        screenshot: '/screenshots/feature-camp-2.png'
      },
      {
        title: 'Track Conversions',
        description: 'Monitor how many requests were sent, opened, and converted into actual Google & Facebook reviews.',
        screenshot: '/screenshots/feature-camp-3.png'
      }
    ]
  },
  'feedback-inbox': {
    slug: 'feedback-inbox',
    title: 'Feedback Inbox',
    subtitle: 'Handle private complaints securely.',
    description: 'Intercept negative feedback before it hits Google & Facebook using our Review Gate and manage it in a private inbox.',
    importance: 'Handling complaints publicly can damage your brand\'s image. A private inbox allows you to resolve issues directly with the customer, turning a potentially viral negative review into a private customer service win.',
    icon: Inbox,
    steps: [
      {
        title: 'View Private Feedback',
        description: 'When an unhappy customer submits private feedback, it lands in your dedicated Feedback Inbox.',
        screenshot: '/screenshots/feature-inbox-1.png'
      },
      {
        title: 'Respond to Customers',
        description: 'Reply directly to the customer via email to resolve their issue privately.',
        screenshot: '/screenshots/feature-inbox-2.png'
      },
      {
        title: 'Mark as Resolved',
        description: 'Keep your team aligned by marking tickets as resolved once the customer is satisfied.',
        screenshot: '/screenshots/feature-inbox-3.png'
      }
    ]
  },
  'review-gate': {
    slug: 'review-gate',
    title: 'Review Gate Funnel',
    subtitle: 'Filter the bad, amplify the good.',
    description: 'Create a branded landing page that asks customers about their experience. Happy customers are sent to Google, while unhappy ones are directed to a private form.',
    importance: 'Review gating acts as a digital bouncer for your reputation. It maximizes your public 5-star ratings by funneling happy customers to Google, while intercepting unhappy customers so you can perform service recovery offline.',
    icon: Filter,
    steps: [
      {
        title: 'Customize Your Funnel',
        description: 'Upload your logo, set your brand colors, and customize the welcome message.',
        screenshot: '/screenshots/feature-gate-1.png'
      },
      {
        title: 'Set Routing Logic',
        description: 'Determine the threshold (e.g., 4 stars and up go to Google; 3 stars and below go to private feedback).',
        screenshot: '/screenshots/feature-gate-2.png'
      },
      {
        title: 'Share Your Link',
        description: 'Generate a QR code or short link to share on receipts, tables, or follow-up emails.',
        screenshot: '/screenshots/feature-gate-3.png'
      }
    ]
  },
  'team-management': {
    slug: 'team-management',
    title: 'Team Management & Roles',
    subtitle: 'Collaborate securely.',
    description: 'Invite your staff to manage reviews without giving them access to billing or sensitive settings.',
    importance: 'As your business grows, you cannot reply to every review yourself. Granular permissions allow you to delegate reputation management securely, ensuring staff can reply without exposing your sensitive billing or location settings.',
    icon: Users,
    steps: [
      {
        title: 'Invite Team Members',
        description: 'Go to Settings > Team and send email invitations to your managers or staff.',
        screenshot: '/screenshots/feature-team-1.png'
      },
      {
        title: 'Assign Roles',
        description: 'Assign roles like "Admin", "Manager", or "Responder" to restrict permissions appropriately.',
        screenshot: '/screenshots/feature-team-2.png'
      },
      {
        title: 'Audit Log',
        description: 'Keep track of who published which reply and when, ensuring full accountability.',
        screenshot: '/screenshots/feature-team-3.png'
      }
    ]
  }
};

export default function FeaturePage() {
    const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const data = slug ? featuresData[slug] || null : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slug) trackFeaturePageView(slug);
  }, [slug]);

  if (!data) {
    return (
      <div className="fp-not-found">
        <h2>{t('feature.featureNotFound')}</h2>
        <Link to="/">{t('feature.returnToHome')}</Link>
      </div>
    );
  }

  const IconComponent = data.icon;

  return (
    <>
      <SEO 
        title={`${data.title} | Reviio Features`}
        description={data.description}
      />
      
      {/* Navbar */}
      <SiteHeader variant="solid" />

      <main className="fp-main">
        {/* Hero Section */}
        <header className="fp-hero">
          <div className="lp-container">
            <div className="fp-icon-wrap">
              <IconComponent size={40} color="#fff" />
            </div>
            <h1>{data.title}</h1>
            <p className="fp-subtitle">{data.subtitle}</p>
            <p className="fp-desc">{data.description}</p>
          </div>
        </header>

        {/* Importance Section */}
        <section className="fp-importance-section">
          <div className="lp-container">
            <div className="fp-importance-card">
              <h2>{t('feature.whyThisMatters')}</h2>
              <p>{data.importance}</p>
            </div>
          </div>
        </section>

        {/* Steps Section */}
        <section className="fp-steps-section">
          <div className="lp-container">
            <h2 className="fp-section-title">{t('feature.howItWorks')}</h2>
            <div className="fp-steps-list">
              {data.steps.map((step, index) => (
                <div className="fp-step-card" key={index}>
                  <div className="fp-step-content">
                    <div className="fp-step-number">{index + 1}</div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                  {step.screenshot && (
                    <div className="fp-step-image">
                      <img src={step.screenshot} alt={`Screenshot of ${step.title}`} loading="lazy" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="lp-cta-banner">
          <div className="lp-container">
            <h2>{`${t('feature.readyToUse')}${data.title}?`}</h2>
            <p>{t('feature.joinHundredsOfBusinessesThatNever')}</p>
            <div className="lp-cta-banner-actions">
              <Link to="/signup" className="lp-btn-primary lp-btn-primary-lg">{t('common.claimYourFreeTrial')}</Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <SiteFooter />
    </>
  );
}
